import { Link as FplLink, Text } from '@figma/fpl-components';
import { Icon24ActionGo, Icon24PlaySolid } from '@figma/fpl-icons';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useLinkClickHandler } from 'react-router-dom';
import { generatorModePath } from '@/components/Generate/generatorPath';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { I18N_KEYS } from '@/language/keys';
import imagesBack from './assets/generator-images-back.webp';
import imagesFront from './assets/generator-images-front.webp';
import imagesMiddle from './assets/generator-images-middle.webp';
import videoPicture from './assets/generator-video.webp';
import { MAKERS_IN_ORDER, MakerLogo } from './ModelRow';
import { cardMarker, toolkitLayout } from './toolkit.stylex';
import type { ReactNode } from 'react';
import type { ModelMaker } from '@/types/toolkit.types';

const ease = 'cubic-bezier(0.2, 0.65, 0.3, 1)';

const styles = stylex.create({
  panel: {
    backgroundColor: color.bgSecondary,
    borderRadius: borderRadius.large,
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacer.spacer4,
    paddingBlock: spacer.spacer3,
    paddingLeft: spacer.spacer4,
    paddingRight: spacer.spacer3,
  },
  intro: {
    display: 'flex',
    flexBasis: '240px',
    flexDirection: 'column',
    flexGrow: 1,
    gap: spacer.spacer3,
    justifyContent: 'space-between',
  },
  text: {
    margin: 0,
  },
  logos: {
    display: 'flex',
  },
  // The logos overlap a little. The border has the panel's color, so each one looks cut out of the next.
  logo: {
    alignItems: 'center',
    backgroundColor: color.bg,
    borderColor: color.bgSecondary,
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    display: 'flex',
    height: '32px',
    justifyContent: 'center',
    marginRight: {
      default: '-4px',
      ':last-child': 0,
    },
    width: '32px',
  },
  cards: {
    display: 'flex',
    flexBasis: '576px',
    flexGrow: 2,
    flexWrap: 'wrap',
    gap: toolkitLayout.cardGap,
  },
  card: {
    borderColor: color.border,
    borderRadius: borderRadius.large,
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    display: 'flex',
    flexBasis: toolkitLayout.entryCardMinWidth,
    flexDirection: 'column',
    flexGrow: 1,
    isolation: 'isolate',
    justifyContent: 'space-between',
    maxWidth: toolkitLayout.entryCardMaxWidth,
    minHeight: '138px',
    overflow: 'hidden',
    padding: spacer.spacer3,
    position: 'relative',
  },
  // The design's gradients have no token, so each color mode has its own.
  cardLight: {
    backgroundImage: 'linear-gradient(-38deg, rgb(245 245 245) 23%, rgb(255 255 255) 97%)',
  },
  cardDark: {
    backgroundImage: 'linear-gradient(-19deg, rgb(30 30 30) 14%, rgb(63 59 59) 129%)',
  },
  // Keeps the words clear of the pictures, which sit on the card's right side.
  words: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'calc(100% - 140px)',
  },
  // The pictures sit behind the words and are cut by the card's right and bottom edges, as in the design.
  art: {
    height: '150px',
    position: 'absolute',
    right: '-17px',
    top: '32px',
    width: '150px',
    zIndex: -1,
  },
  picture: {
    borderRadius: borderRadius.large,
    height: '113px',
    left: '18px',
    objectFit: 'cover',
    position: 'absolute',
    top: '18px',
    transition: {
      default: `transform 0.4s ${ease}`,
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
    width: '113px',
  },
  resting: {
    transform: 'rotate(-25deg)',
  },
  // On hover the three image pictures fan out, as in the design's hover state.
  fanBack: {
    transform: {
      default: 'rotate(-25deg)',
      [stylex.when.ancestor(':hover', cardMarker)]: 'translate(-19px, 21px) rotate(-27deg)',
    },
  },
  fanFront: {
    transform: {
      default: 'rotate(-25deg)',
      [stylex.when.ancestor(':hover', cardMarker)]: 'translate(28px, -19px) rotate(-22deg)',
    },
  },
  play: {
    '--fpl-icon-color': 'white',
    alignItems: 'center',
    backgroundColor: 'rgb(86 86 86 / 0.5)',
    borderRadius: '50%',
    display: 'flex',
    left: '50%',
    padding: spacer.spacer1,
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%) rotate(-25deg)',
  },
});

interface EntryCardProps {
  title: string;
  description: string;
  action: string;
  href: string;
  art: ReactNode;
}

function EntryCard({ title, description, action, href, art }: EntryCardProps) {
  const isDark = useThemeMode() === 'dark';
  // In-app navigation, so the generator reuses the model list the Toolkit loaded for it.
  const handleClick = useLinkClickHandler(href);

  return (
    <div {...stylex.props(styles.card, isDark ? styles.cardDark : styles.cardLight, cardMarker)}>
      <div {...stylex.props(styles.words)}>
        <Text.h3 {...stylex.props(styles.text)} variant="heading-small" color="default">
          {title}
        </Text.h3>
        <Text.p {...stylex.props(styles.text)} variant="body-medium" color="secondary">
          {description}
        </Text.p>
      </div>
      <div>
        <FplLink.Button href={href} onClick={handleClick} variant="primary" iconPrefix={<Icon24ActionGo />}>
          {action}
        </FplLink.Button>
      </div>
      <span {...stylex.props(styles.art)} aria-hidden>
        {art}
      </span>
    </div>
  );
}

interface GeneratorEntryProps {
  /** The makers of the models on the model row. Only their logos are shown. */
  makers: ReadonlySet<ModelMaker>;
}

/** The panel at the top of the Toolkit with the two ways into the generator: one for images and one for video. */
export function GeneratorEntry({ makers }: GeneratorEntryProps) {
  const { t } = useTranslation();
  const title = t(I18N_KEYS.TOOLKIT.GENERATOR_TITLE);
  const logos = MAKERS_IN_ORDER.filter((maker) => makers.has(maker));

  return (
    <section {...stylex.props(styles.panel)} aria-label={title}>
      <div {...stylex.props(styles.intro)}>
        <div>
          <Text.h2 {...stylex.props(styles.text)} variant="heading-small" color="default">
            {title}
          </Text.h2>
          <Text.p {...stylex.props(styles.text)} variant="body-medium" color="secondary">
            {t(I18N_KEYS.TOOLKIT.GENERATOR_SUBTITLE)}
          </Text.p>
        </div>
        <div {...stylex.props(styles.logos)} aria-hidden>
          {logos.map((maker) => (
            <span key={maker} {...stylex.props(styles.logo)}>
              <MakerLogo maker={maker} />
            </span>
          ))}
        </div>
      </div>
      <div {...stylex.props(styles.cards)}>
        <EntryCard
          title={t(I18N_KEYS.TOOLKIT.GENERATOR_IMAGES_TITLE)}
          description={t(I18N_KEYS.TOOLKIT.GENERATOR_IMAGES_DESCRIPTION)}
          action={t(I18N_KEYS.TOOLKIT.GENERATOR_IMAGES_ACTION)}
          href={generatorModePath('image')}
          art={
            <>
              <img {...stylex.props(styles.picture, styles.fanBack)} src={imagesBack} alt="" />
              <img {...stylex.props(styles.picture, styles.resting)} src={imagesMiddle} alt="" />
              <img {...stylex.props(styles.picture, styles.fanFront)} src={imagesFront} alt="" />
            </>
          }
        />
        <EntryCard
          title={t(I18N_KEYS.TOOLKIT.GENERATOR_VIDEOS_TITLE)}
          description={t(I18N_KEYS.TOOLKIT.GENERATOR_VIDEOS_DESCRIPTION)}
          action={t(I18N_KEYS.TOOLKIT.GENERATOR_VIDEOS_ACTION)}
          href={generatorModePath('video')}
          art={
            <>
              <img {...stylex.props(styles.picture, styles.resting)} src={videoPicture} alt="" />
              <span {...stylex.props(styles.play)}>
                <Icon24PlaySolid />
              </span>
            </>
          }
        />
      </div>
    </section>
  );
}
